/**
 * useChatAttachments — the file-attachment pipeline lifted out of ChatBox
 * (delegation pattern). The useImport hook call + handleFileSelect + handlePaste
 * (incl. the paste-guard chip path) + handleChatDragOver + handleChatDrop are
 * reproduced VERBATIM from ChatBox: same bodies, same byte-identical dependency
 * arrays. uploadFilesToAssets stays factored in utils/chat-attachments.
 *
 * useImport is a hook, so this whole cluster is a CONTIGUOUS slice — ChatBox
 * calls useChatAttachments at the SAME body position the inline cluster
 * occupied, preserving hook count/order exactly. Setters
 * (setPendingAttachments / setPastedAttachments / setStorageExceeded) are
 * threaded in; everything else closes over the threaded values. NO raw fetch —
 * keeps the api.* / apiFetch usage inside useImport + uploadFilesToAssets.
 */
import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { decidePasteGuard } from '@/features/chat/utils/paste-guard';
import { MENTION_DRAG_MIME, parseDroppedReference, type MentionReference } from '@/features/chat/utils/mention-parser';
import { useImport, type ImportPhase, type ImportFileStatus } from '@/features/chat/hooks/useImport';
import { uploadFilesToAssets } from '@/features/chat/utils/chat-attachments';
import { PASTE_ATTACHMENT_THRESHOLD_BYTES } from '../components/chatbox/constants';
import type { PendingAttachment, PastedAttachment } from '../components/chatbox/types';
import type { UseChatReturn } from '@/features/chat/hooks/useChat.types';
import type { Conversation } from '@/types/conversations';

export interface UseChatAttachmentsDeps {
  activeConversation: Conversation | null;
  token: string | null;
  workspaceId: string;
  queryClient: QueryClient;
  addAssistantMessage: UseChatReturn['addAssistantMessage'];
  setPendingAttachments: Dispatch<SetStateAction<PendingAttachment[]>>;
  setPastedAttachments: Dispatch<SetStateAction<PastedAttachment[]>>;
  setStorageExceeded: Dispatch<SetStateAction<boolean>>;
  /** Splice a dragged object reference into the composer (issue #1457).
   *  Owned by useMentionAutocomplete; called from handleChatDrop when the drop
   *  carries an object payload instead of files. */
  applyDroppedReference: (ref: MentionReference) => void;
}

export interface UseChatAttachmentsResult {
  importFiles: ReturnType<typeof useImport>['importFiles'];
  importPhase: ImportPhase;
  importingFiles: ImportFileStatus[];
  importProgress: { imported: number; total: number };
  isImporting: boolean;
  resetImport: () => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  handleChatDragOver: (e: React.DragEvent) => void;
  handleChatDrop: (e: React.DragEvent) => void;
}

export function useChatAttachments(deps: UseChatAttachmentsDeps): UseChatAttachmentsResult {
  const {
    activeConversation,
    token,
    workspaceId,
    queryClient,
    addAssistantMessage,
    setPendingAttachments,
    setPastedAttachments,
    setStorageExceeded,
    applyDroppedReference,
  } = deps;

  // Import hook for file uploads
  const {
    importFiles,
    phase: importPhase,
    files: importingFiles,
    progress: importProgress,
    isImporting,
    reset: resetImport,
  } = useImport({
    conversationId: activeConversation?.id || '',
    onComplete: () => {
      // Invalidate conversation data to refresh modules (don't auto-add message — user controls the flow)
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(activeConversation?.id || '') });
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
    },
    onError: (error) => {
      addAssistantMessage(`Import failed: ${error}`, activeConversation?.id);
      // Clear any still-uploading attachments on error
      setPendingAttachments([]);
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !activeConversation?.id) {
      event.target.value = '';
      return;
    }

    const fileArray = Array.from(files);
    const conversationId = activeConversation.id;

    // Add inline previews immediately
    const newAttachments: PendingAttachment[] = fileArray.map(f => ({
      id: `attach-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      filename: f.name,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
      fileType: f.type,
      file: f,
    }));
    setPendingAttachments(prev => [...prev, ...newAttachments]);

    // Upload to API as assets (fire-and-forget — updates attachment entries with asset info)
    uploadFilesToAssets(fileArray, conversationId, token, workspaceId).then(uploaded => {
      if (!uploaded.length) return;
      setPendingAttachments(prev => prev.map(att => {
        const idx = newAttachments.findIndex(na => na.id === att.id);
        if (idx === -1) return att;
        const asset = uploaded[idx];
        if (!asset) return att;
        return { ...att, assetId: asset.id, assetUrl: asset.attributes?.storageUrl };
      }));
      // Refresh asset modules so newly-uploaded images appear
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('asset') });
    }).catch((err) => {
      if ((err as Error & { storageLimitExceeded?: boolean }).storageLimitExceeded) {
        setStorageExceeded(true);
        return;
      }
      console.error('[uploadFilesToAssets] catch:', err);
    });

    // Start import in background; preview stays visible until user sends
    importFiles(files).catch(() => {
      // onError callback handles the message; remove the previews
      const ids = new Set(newAttachments.map(a => a.id));
      setPendingAttachments(prev => prev.filter(a => !ids.has(a.id)));
    });

    // Reset so same file can be selected again
    event.target.value = '';
  };

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length > 0 && activeConversation?.id) {
      e.preventDefault();
      const dt = new DataTransfer();
      imageFiles.forEach(f => dt.items.add(f));

      // Add inline previews
      const newAttachments: PendingAttachment[] = imageFiles.map(f => ({
        id: `attach-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        filename: f.name || 'pasted-image.png',
        previewUrl: URL.createObjectURL(f),
        fileType: f.type,
        file: f,
      }));
      const conversationId = activeConversation.id;
      setPendingAttachments(prev => [...prev, ...newAttachments]);

      // Upload to API as assets
      uploadFilesToAssets(imageFiles, conversationId, token, workspaceId).then(uploaded => {
        if (!uploaded.length) return;
        setPendingAttachments(prev => prev.map(att => {
          const idx = newAttachments.findIndex(na => na.id === att.id);
          if (idx === -1) return att;
          const asset = uploaded[idx];
          if (!asset) return att;
          return { ...att, assetId: asset.id, assetUrl: asset.attributes?.storageUrl };
        }));
        queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('asset') });
      }).catch((err) => {
        if ((err as Error & { storageLimitExceeded?: boolean }).storageLimitExceeded) {
          setStorageExceeded(true);
          return;
        }
        console.error('[uploadFilesToAssets] catch:', err);
      });

      importFiles(dt.files).catch(() => {
        const ids = new Set(newAttachments.map(a => a.id));
        setPendingAttachments(prev => prev.filter(a => !ids.has(a.id)));
      });
      return;
    }

    // Paste guard: when the clipboard contains ONLY text and the text is
    // larger than the attachment threshold, intercept the paste and create
    // a collapsed chip instead of inlining into the textarea. Small pastes
    // fall through to the browser default.
    //
    // Why: inlining a >4KB HTML block into the user turn blows the prompt
    // assembler's token budget. Diagnostic fixtures traced a silent loop
    // in context assembly that emitted bare "Opened it." / "Done." in
    // place of real work — see the page-conv fixture. The decision is a
    // pure function in `paste-guard.ts`.
    const pastedText = e.clipboardData?.getData('text/plain') ?? '';
    const decision = decidePasteGuard(pastedText, PASTE_ATTACHMENT_THRESHOLD_BYTES);
    if (!decision.attach) return;

    e.preventDefault();
    const chip: PastedAttachment = {
      id: `paste-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      content: pastedText,
      label: decision.label!,
      size: decision.byteSize,
    };
    setPastedAttachments(prev => [...prev, chip]);
  }, [activeConversation?.id, importFiles, setPendingAttachments, token, workspaceId]);

  // Drag-and-drop files into chat
  const handleChatDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleChatDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();

    // Object-reference drop (issue #1457): an object dragged from a module pane
    // carries a JSON MentionReference under the vendor MIME (falling back to
    // text/plain). Splice it into the composer as a mention marker and stop —
    // this is NOT a file drop.
    const refRaw = e.dataTransfer?.getData(MENTION_DRAG_MIME)
      || e.dataTransfer?.getData('text/plain');
    const droppedRef = parseDroppedReference(refRaw);
    if (droppedRef) {
      applyDroppedReference(droppedRef);
      return;
    }

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0 || !activeConversation?.id) return;

    const fileArray = Array.from(files);
    const conversationId = activeConversation.id;

    const newAttachments: PendingAttachment[] = fileArray.map(f => ({
      id: `attach-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      filename: f.name,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
      fileType: f.type,
      file: f,
    }));
    setPendingAttachments(prev => [...prev, ...newAttachments]);

    uploadFilesToAssets(fileArray, conversationId, token, workspaceId).then(uploaded => {
      if (!uploaded.length) return;
      setPendingAttachments(prev => prev.map(att => {
        const idx = newAttachments.findIndex(na => na.id === att.id);
        if (idx === -1) return att;
        const asset = uploaded[idx];
        if (!asset) return att;
        return { ...att, assetId: asset.id, assetUrl: asset.attributes?.storageUrl };
      }));
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('asset') });
    }).catch((err) => {
      if ((err as Error & { storageLimitExceeded?: boolean }).storageLimitExceeded) {
        setStorageExceeded(true);
        return;
      }
      console.error('[uploadFilesToAssets] catch:', err);
    });

    // Only run importFiles for non-image files (images are handled by uploadFilesToAssets)
    const nonImageFiles = fileArray.filter(f => !f.type.startsWith('image/'));
    if (nonImageFiles.length > 0) {
      const dt = new DataTransfer();
      nonImageFiles.forEach(f => dt.items.add(f));
      importFiles(dt.files).catch(() => {
        const ids = new Set(newAttachments.map(a => a.id));
        setPendingAttachments(prev => prev.filter(a => !ids.has(a.id)));
      });
    }
  }, [activeConversation?.id, importFiles, setPendingAttachments, token, workspaceId, applyDroppedReference]);

  return {
    importFiles,
    importPhase,
    importingFiles,
    importProgress,
    isImporting,
    resetImport,
    handleFileSelect,
    handlePaste,
    handleChatDragOver,
    handleChatDrop,
  };
}
